import React, { useEffect, useState } from "react";
import { api, API, formatApiError } from "../lib/api";
import { FileCheck2, Plus, Loader2, Pencil, Trash2, FileText, X, Building2, ChevronDown, ChevronRight, Image as ImageIcon, Package, Link as LinkIcon, Eye, AlertTriangle, ShieldCheck, Zap, Snowflake, Thermometer, CloudUpload, CheckCircle2, CloudDownload } from "lucide-react";
import { toast } from "sonner";
import { DicoAssistenteButton, DicoAiNote } from "./DicoAssistente";

const TIPOLOGIE = [
  ["fotovoltaico", "Impianto Fotovoltaico", Zap, "#F59E0B"],
  ["pompa_calore", "Pompa di calore", Snowflake, "#3B82F6"],
  ["climatizzazione", "Impianto di climatizzazione", Thermometer, "#10B981"],
];

const INTERVENTI = [
  ["nuova_installazione", "Nuova installazione"],
  ["trasformazione", "Trasformazione"],
  ["ampliamento", "Ampliamento"],
  ["manutenzione_straordinaria", "Manutenzione straordinaria"],
  ["altro", "Altro"],
];

const ALLEGATI = [
  ["all_relazione_materiali", "Relazione tipologie materiali (art. 7)"],
  ["all_schema", "Schema di impianto realizzato"],
  ["all_attestazione_requisiti", "Certificato requisiti tecnico-professionali"],
  ["all_progetto", "Progetto (artt. 5 e 7)"],
  ["all_riferimento_dico_precedenti", "Riferimento a DiCo precedenti/parziali"],
];

const VERIFICHE = [
  ["ver_continuita", "Continuità conduttori di protezione"],
  ["ver_polarita", "Polarità corretta"],
  ["ver_serraggio", "Serraggio connessioni"],
  ["ver_differenziali", "Prova dispositivi differenziali"],
  ["ver_coordinamento", "Verifica coordinamento protezioni"],
  ["ver_circuiti", "Identificazione circuiti"],
  ["ver_potere", "Potere di interruzione verificato"],
  ["ver_caduta", "Caduta tensione controllata"],
];

const VERIFICHE_PDC = [
  ["ver_pdc_componenti_conformi", "Vengono elencati i componenti installati nell'impianto, conformi a quanto previsto dall'art. 7 D.M. 37/08"],
  ["ver_pdc_compatibilita_esistente", "L'impianto è compatibile con gli impianti preesistenti"],
  ["ver_pdc_componenti_idonei", "I componenti sono idonei rispetto all'ambiente di installazione"],
];

const VERIFICHE_CLIMA = [
  ["ver_clima_tenuta_frigo", "Prova di tenuta circuito frigorifero"],
  ["ver_clima_vuoto_impianto", "Vuoto dell'Impianto"],
  ["ver_clima_verifica_funzionale", "Verifica funzionale delle unità"],
  ["ver_clima_scarico_condensa", "Controllo corretto scarico condensa"],
];

const IMPRESA_TEXT_FIELDS = [
  { section: "Anagrafica", fields: [
    ["ragione_sociale", "Ragione sociale"],
    ["settore", "Settore (es. impianti elettrici)"],
    ["piva", "P.IVA"],
    ["cf", "Codice Fiscale"],
    ["cciaa_provincia", "CCIAA di (provincia)"],
    ["rea_numero", "Numero REA"],
    ["sdi", "Codice SDI"],
  ]},
  { section: "Sede legale", fields: [
    ["sede_indirizzo", "Indirizzo sede legale"],
    ["sede_cap", "CAP"],
    ["sede_comune", "Comune"],
    ["sede_provincia", "Provincia"],
  ]},
  { section: "Contatti / Ufficio tecnico", fields: [
    ["pec", "PEC"],
    ["email", "Email"],
    ["telefono", "Telefono"],
    ["sede_ufficio_tecnico", "Ufficio tecnico (indirizzo completo)"],
    ["footer_note", "Nota footer PDF (es. \"Documento generato da Ufficio Tecnico\")"],
  ]},
  { section: "Legale rappresentante & Responsabile Tecnico", fields: [
    ["titolare", "Titolare / Legale rappresentante"],
    ["responsabile_tecnico", "Responsabile Tecnico (nome completo)"],
    ["albo_resp_tecnico", "Albo/Ordine RT (es. Ordine Architetti Roma)"],
    ["iscrizione_resp_tecnico", "N° iscrizione RT"],
  ]},
];

const CATEGORIE = [
  ["modulo", "Modulo Fotovoltaico"],
  ["inverter", "Inverter"],
  ["accumulo", "Sistema di Accumulo"],
  ["wallbox", "Wallbox / EV Charger"],
  ["pompa_calore", "Pompa di Calore"],
  ["bollitore", "Bollitore / ACS"],
  ["unita_interna", "Unità Interna (climatizzazione)"],
  ["unita_esterna", "Unità Esterna (climatizzazione)"],
  ["quadro", "Quadro Elettrico AC/DC"],
  ["protezione", "Protezione (interruttore, differenziale)"],
  ["cavo", "Cavo / Linea elettrica"],
  ["terra", "Impianto di Terra"],
  ["altro", "Altro"],
];

// Sotto-tipologia dell'impianto fotovoltaico: determina quali sotto-sezioni
// dei componenti installati sono visibili nel form e mostrate nel PDF.
const SOTTO_TIPOLOGIE_FV = [
  ["fotovoltaico_completo", "Impianto Fotovoltaico completo"],
  ["solo_inverter", "Solo Inverter"],
  ["solo_accumulo", "Solo Accumulo"],
  ["inverter_accumulo", "Inverter + Accumulo (no pannelli)"],
];

// Sotto-tipologia della pompa di calore (Tipo Impianto)
const SOTTO_TIPOLOGIE_PDC = [
  ["pompa_calore", "Pompa di calore"],
  ["caldaia_ibrida", "Caldaia a gas con generatore di calore ibrido a pompa di calore per produzione di ACS e riscaldamento"],
  ["pompa_calore_aria_acqua", "Pompa di calore aria/acqua"],
];

// ---------- Schema campi per categoria (deve corrispondere a CATEGORY_SCHEMAS del backend) ----------
// kind: "text" = input testo, "num" = numero, "check_esso" = ☒ Eseguito, "check_ver" = ☒ Verificata
const CATEGORY_FIELDS = {
  modulo: [
    ["quantita", "Numero moduli installati", "num"],
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["potenza_wp", "Potenza singolo modulo (Wp)", "num"],
    ["potenza_totale_kw", "Potenza totale generatore (kWp) — lascia vuoto per calcolo automatico", "num"],
    ["connettori", "Connettori (es. MC4)", "text"],
    ["struttura", "Struttura di supporto", "text"],
  ],
  inverter: [
    ["quantita", "Numero unità installate", "num"],
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["potenza_kw", "Potenza nominale (kW)", "num"],
    ["tensione_ac", "Tensione nominale AC", "text"],
    ["corrente_ac", "Corrente max uscita AC", "text"],
    ["dispositivo_interfaccia", "Dispositivo interfaccia (es. SPI integrato CEI 0-21)", "text"],
  ],
  accumulo: [
    ["quantita", "Numero unità installate", "num"],
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["capacita_kwh", "Capacità singola unità (kWh)", "num"],
    ["capacita_totale_kwh", "Capacità totale (kWh) — vuoto = calcolo automatico", "num"],
    ["tecnologia_celle", "Tecnologia celle (es. LFP)", "text"],
    ["collegamento_conforme", "Collegamento conforme a (es. CEI 0-21)", "text"],
  ],
  quadro: [
    ["involucri", "Involucri (grado IP, materiale)", "text"],
    ["corrente_nominale", "Corrente nominale DG/DDG", "text"],
    ["coordinamento", "Coordinamento protezioni", "text"],
    ["identificazione", "Identificazione circuiti", "text"],
  ],
  wallbox: [
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["quantita", "Numero unità installate", "num"],
    ["potenza_kw", "Potenza (kW)", "num"],
  ],
  pompa_calore: [
    ["quantita", "N° unità", "num"],
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["potenza_kw", "Potenza nominale (kW)", "num"],
    ["gas_refrigerante", "Gas Refrigerante (es. R32, R410A)", "text"],
    ["cop", "COP", "text"],
    ["scop", "SCOP Stagionale", "text"],
  ],
  bollitore: [
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["capacita_litri", "Capacità (Litri)", "num"],
  ],
  unita_interna: [
    ["n_split", "N° unità split", "num"],
    ["gas_refrigerante", "Gas refrigerante (es. R32, R410A)", "text"],
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
  ],
  unita_esterna: [
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["potenza_frigorifera_kw", "Potenza frigorifera Cad. (kW)", "num"],
    ["potenza_termica_kw", "Potenza Termica Cad. (kW)", "num"],
  ],
  protezione: [
    ["interruttore_dg", "Interruttore DG", "text"],
    ["differenziale_ddg", "Protezione differenziale DDG", "text"],
    ["potere_interruzione", "Potere di interruzione", "text"],
    ["coordinamento", "Coordinamento DG-conduttori", "text"],
  ],
  cavo: [
    ["cavo_dc", "Cavo DC (tipo, sezione)", "text"],
    ["cavo_ac", "Cavo AC (tipo, sezione)", "text"],
    ["cavo_pe", "Conduttore PE", "text"],
    ["tubo_diametro", "Tubo / Diametro", "text"],
    ["posa", "Tipo Posa", "text"],
  ],
  terra: [
    ["equipotenziale", "Collegamento equipotenziale strutture", "check_esso"],
    ["terra_esistente", "Collegamento a impianto di terra esistente", "check_esso"],
    ["continuita_pe", "Continuità conduttore di protezione", "check_ver"],
    ["normativa", "Coordinamento con prot. differenziale — Norma (es. CEI 64-8)", "text"],
  ],
  altro: [
    ["marca", "Marca", "text"],
    ["modello", "Modello", "text"],
    ["quantita", "Quantità", "num"],
    ["note", "Note", "text"],
    ["extra", "Dati aggiuntivi", "text"],
  ],
};

const inputCls = "w-full px-3 py-2 text-sm rounded-md border outline-none";
const inputStyle = { borderColor: "var(--border)", background: "#fff" };

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>{label}</label>
    {children}
  </div>
);

// ---------- Accordion ----------
function Accordion({ title, defaultOpen = false, icon: Icon = ChevronRight, testid, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-md" style={{ borderColor: "var(--border)" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold hover:bg-black/5"
        style={{ fontFamily: "Manrope" }}
        data-testid={testid ? `dico-acc-${testid}` : undefined}
      >
        <span className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          {title}
          {badge != null && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "var(--primary)", color: "#fff" }}>{badge}</span>}
        </span>
      </button>
      {open && <div className="px-4 pb-4 pt-2 border-t" style={{ borderColor: "var(--border)" }}>{children}</div>}
    </div>
  );
}

// ---------- URL image input ----------
function ImageUrlInput({ label, value, onChange, testid, hint }) {
  const [preview, setPreview] = useState(value || "");
  useEffect(() => { setPreview(value || ""); }, [value]);
  return (
    <div>
      <label className="text-xs font-semibold block mb-1" style={{ color: "var(--text-muted)" }}>{label}</label>
      <div className="flex items-start gap-3">
        <div className="w-32 h-20 rounded-md border flex items-center justify-center overflow-hidden bg-white flex-shrink-0" style={{ borderColor: "var(--border)" }}>
          {preview ? (
            <img src={preview} alt={label} className="max-w-full max-h-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : <ImageIcon className="w-6 h-6 opacity-30" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-1">
            <LinkIcon className="w-3 h-3 opacity-60" />
            <input
              type="url"
              placeholder="https://…/immagine.png"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              className={inputCls}
              style={inputStyle}
              data-testid={testid}
            />
          </div>
          {hint && <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{hint}</div>}
          {value && (
            <button type="button" onClick={() => onChange("")} className="text-[11px] text-red-600 mt-1" data-testid={`${testid}-clear`}>Rimuovi URL</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Impresa Modal ----------
export function ImpresaModal({ onClose }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/dico/impresa").then(({ data }) => setF(data)).catch(() => setF({})); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dico/impresa", f);
      toast.success("Dati impresa salvati");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="dico-impresa-modal">
      <div className="card-shell w-full max-w-4xl p-6 max-h-[92vh] overflow-y-auto" style={{ background: "#fff" }}>
        <div className="flex items-start justify-between mb-3">
          <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>
            <Building2 className="w-5 h-5 inline mr-2" />Dati impresa installatrice
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-black/5" data-testid="dico-impresa-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-4 p-3 rounded-md flex items-start gap-2 text-xs" style={{ background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" }} data-testid="dico-impresa-global-notice">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <b>Impostazioni globali</b> — questi dati (anagrafica, logo, firme, visura camerale) sono <b>unici per tutta l&apos;azienda</b> e vengono usati automaticamente in tutte le DiCo di tutti i clienti. Vanno compilati una sola volta.
          </div>
        </div>
        {!f ? <Loader2 className="w-5 h-5 animate-spin" /> : (
          <div className="space-y-3">
            {IMPRESA_TEXT_FIELDS.map((sec) => (
              <Accordion key={sec.section} title={sec.section} defaultOpen testid={`imp-${sec.section}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  {sec.fields.map(([k, lbl]) => (
                    <Field key={k} label={lbl}>
                      <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} style={inputStyle} data-testid={`dico-impresa-${k}`} />
                    </Field>
                  ))}
                </div>
              </Accordion>
            ))}
            <Accordion title="Logo, firme e timbri (URL immagini)" defaultOpen testid="imp-images">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                <ImageUrlInput label="URL Logo azienda"
                  value={f.logo_url || ""} onChange={(v) => setF({ ...f, logo_url: v })}
                  testid="dico-logo-url"
                  hint="URL pubblica dell'immagine (PNG/JPG). Consigliato sfondo trasparente." />
                <ImageUrlInput label="URL Firma + timbro Azienda"
                  value={f.firma_impresa_url || ""} onChange={(v) => setF({ ...f, firma_impresa_url: v })}
                  testid="dico-firma-imp-url" />
                <ImageUrlInput label="URL Firma + timbro Resp. Tecnico"
                  value={f.firma_rt_url || ""} onChange={(v) => setF({ ...f, firma_rt_url: v })}
                  testid="dico-firma-rt-url" />
              </div>
              <div className="text-xs mt-3 p-3 rounded-md" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>
                💡 <b>Come ottenere l&apos;URL immagine?</b> Carica il file su Aruba Drive, Google Drive (link pubblico), il tuo sito, WordPress o un servizio come Imgur, poi incolla qui il link diretto (deve terminare con .png, .jpg o essere accessibile pubblicamente). Le immagini non vengono salvate nel database, così l&apos;app resta leggera.
              </div>
            </Accordion>

            <Accordion title="Visura Camerale (allegata al PDF DiCo)" defaultOpen testid="imp-visura">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                <Field label="URL PDF Visura Camerale">
                  <div className="flex items-center gap-1">
                    <LinkIcon className="w-3 h-3 opacity-60" />
                    <input type="url" placeholder="https://…/visura.pdf"
                      value={f.visura_url || ""}
                      onChange={(e) => setF({ ...f, visura_url: e.target.value })}
                      className={inputCls} style={inputStyle}
                      data-testid="dico-visura-url" />
                  </div>
                </Field>
                <Field label="Data scadenza visura">
                  <input type="date"
                    value={f.visura_scadenza || ""}
                    onChange={(e) => setF({ ...f, visura_scadenza: e.target.value })}
                    className={inputCls} style={inputStyle}
                    data-testid="dico-visura-scadenza" />
                </Field>
              </div>
              {f.visura_url && (
                <div className="mt-2">
                  <a href={f.visura_url} target="_blank" rel="noreferrer" className="text-xs underline" style={{ color: "var(--primary)" }} data-testid="dico-visura-open">
                    <FileText className="w-3.5 h-3.5 inline mr-1" /> Apri la visura in nuova scheda
                  </a>
                </div>
              )}
              <div className="text-xs mt-3 p-3 rounded-md" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>
                📄 Il PDF della visura camerale (accessibile via URL pubblica) viene <b>automaticamente allegato in coda</b> a ogni PDF DiCo generato. Il sistema mostra un avviso quando mancano 15 giorni alla scadenza indicata.
              </div>
            </Accordion>
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={save} disabled={saving || !f} className="btn-primary" data-testid="dico-impresa-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Componenti Catalogo (per commessa) ----------
function CatalogoBrowser({ commessaId, filterCat, onPick, onClose }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [neu, setNeu] = useState({ categoria: filterCat || "modulo", marca: "", modello: "", quantita: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/dico/componenti/commessa/${commessaId}`);
      setItems(filterCat ? data.filter((c) => c.categoria === filterCat) : data);
    } catch (err) { toast.error(formatApiError(err)); setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    // La validazione stretta di marca/modello non ha senso per tutte le
    // categorie (es. "quadro"/"terra" hanno altri campi caratterizzanti).
    // Basta assicurarsi che almeno un campo utile sia compilato.
    const fields = CATEGORY_FIELDS[neu.categoria] || CATEGORY_FIELDS.altro;
    const hasAny = fields.some(([k]) => (neu[k] || "").toString().trim() !== "");
    if (!hasAny) { toast.error("Compila almeno un campo del componente"); return; }
    try {
      await api.post(`/dico/componenti/commessa/${commessaId}`, neu);
      toast.success("Componente aggiunto al catalogo");
      setNeu({ categoria: filterCat || "modulo" });
      setCreating(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Rimuovere ${c.marca || ""} ${c.modello || ""} dal catalogo?`)) return;
    try { await api.delete(`/dico/componenti/${c.id}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="dico-catalogo-modal">
      <div className="card-shell w-full max-w-3xl p-6 max-h-[85vh] overflow-y-auto" style={{ background: "#fff" }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}><Package className="w-5 h-5 inline mr-2" />Catalogo componenti commessa</div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>I componenti sono salvati per commessa e riutilizzabili in ogni DiCo.</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-black/5" data-testid="dico-cat-close"><X className="w-5 h-5" /></button>
        </div>

        {creating ? (
          <div className="rounded-md border p-3 mb-4" style={{ borderColor: "var(--primary)" }}>
            <div className="font-bold text-sm mb-2">Nuovo componente</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Field label="Categoria">
                <select value={neu.categoria}
                  onChange={(e) => setNeu({ categoria: e.target.value })}
                  className={inputCls} style={inputStyle} data-testid="dico-cat-new-categoria">
                  {CATEGORIE.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                </select>
              </Field>
              {(CATEGORY_FIELDS[neu.categoria] || CATEGORY_FIELDS.altro).map(([k, lbl, kind]) => (
                <Field key={k} label={lbl}>
                  <input
                    type={kind === "num" ? "text" : "text"}
                    value={neu[k] || ""}
                    onChange={(e) => setNeu({ ...neu, [k]: e.target.value })}
                    className={inputCls} style={inputStyle}
                    data-testid={`dico-cat-new-${k}`}
                  />
                </Field>
              ))}
            </div>
            <div className="mt-2 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="btn-secondary text-xs">Annulla</button>
              <button onClick={create} className="btn-primary text-xs" data-testid="dico-cat-new-save">Salva componente</button>
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <button onClick={() => setCreating(true)} className="btn-primary text-xs" data-testid="dico-cat-new-btn">
              <Plus className="w-3.5 h-3.5" /> Nuovo componente
            </button>
          </div>
        )}

        {items === null ? <Loader2 className="w-5 h-5 animate-spin" /> : items.length === 0 ? (
          <div className="text-sm italic py-6 text-center" style={{ color: "var(--text-muted)" }}>Nessun componente nel catalogo di questa commessa.</div>
        ) : (
          <div className="space-y-2">
            {items.map((c) => {
              const catLbl = (CATEGORIE.find(([k]) => k === c.categoria) || [null, c.categoria])[1];
              // Label dinamica: usa i campi definiti da CATEGORY_FIELDS per questa
              // categoria, così anche 'protezione'/'cavo'/'quadro'/'terra' mostrano
              // i loro valori specifici e non appaiono come righe vuote.
              const catFields = CATEGORY_FIELDS[c.categoria] || CATEGORY_FIELDS.altro;
              const spec = catFields
                .map(([k, lbl]) => {
                  const v = c[k];
                  if (v === undefined || v === null || v === "" || v === false) return null;
                  return `${lbl}: ${v === true ? "sì" : v}`;
                })
                .filter(Boolean)
                .slice(0, 3)
                .join(" · ");
              // Fallback per componenti legacy (senza campi custom): marca/modello/qty
              const legacySpec = [c.marca, c.modello, c.quantita ? `x${c.quantita}` : ""].filter(Boolean).join(" · ");
              const displaySpec = spec || legacySpec;
              return (
                <div key={c.id} className="flex items-center gap-3 rounded-md border px-3 py-2" style={{ borderColor: "var(--border)" }} data-testid={`dico-cat-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase font-bold" style={{ color: "var(--primary)" }}>{catLbl}</div>
                    <div className="text-sm font-semibold truncate">{displaySpec || "—"}</div>
                    {c.extra && <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{c.extra}</div>}
                  </div>
                  <button onClick={() => onPick(c)} className="btn-primary text-xs" data-testid={`dico-cat-pick-${c.id}`}>Aggiungi alla DiCo</button>
                  <button onClick={() => remove(c)} className="p-1 rounded hover:bg-black/5" title="Elimina dal catalogo" data-testid={`dico-cat-del-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Sotto-sezione componenti per categoria ----------
function SottoSezioneCategoria({ titolo, categoria, f, commessaId, onOpenCatalogo, onAddBlank, updateComp, removeComp, testid }) {
  const componenti = (f.componenti || []).map((c, idx) => ({ ...c, __idx: idx })).filter((c) => (c.categoria || "modulo") === categoria);
  const fields = CATEGORY_FIELDS[categoria] || CATEGORY_FIELDS.altro;
  return (
    <div className="rounded-md border p-3 mb-3" style={{ borderColor: "var(--border)", background: "#fbfbfd" }} data-testid={`dico-sez-${categoria}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-sm" style={{ fontFamily: "Manrope", color: "var(--primary)" }}>{titolo}</div>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => onOpenCatalogo(categoria)} disabled={!commessaId}
            className="btn-secondary text-xs" data-testid={`${testid}-catalogo`}
            title={!commessaId ? "Cliente senza commessa" : "Scegli dal catalogo commessa"}>
            <Package className="w-3.5 h-3.5" /> Da catalogo
          </button>
          <button type="button" onClick={() => onAddBlank(categoria)} className="btn-secondary text-xs" data-testid={`${testid}-add`}>
            <Plus className="w-3.5 h-3.5" /> Nuovo
          </button>
        </div>
      </div>
      {componenti.length === 0 && (
        <div className="text-xs italic py-2 text-center" style={{ color: "var(--text-muted)" }}>
          Nessun elemento. Aggiungi dal catalogo o crea nuovo.
        </div>
      )}
      <div className="space-y-2">
        {componenti.map((c) => (
          <div key={c.__idx} className="rounded-md border p-2.5 bg-white" style={{ borderColor: "var(--border)" }} data-testid={`dico-comp-${c.__idx}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold flex-1" style={{ color: "var(--text-muted)" }}>{titolo} #{componenti.indexOf(c) + 1}</div>
              {commessaId && (
                <button type="button" onClick={async () => {
                  try { await api.post(`/dico/componenti/commessa/${commessaId}`, c); toast.success("Salvato nel catalogo"); }
                  catch (err) { toast.error(formatApiError(err)); }
                }} className="text-[11px] underline" data-testid={`dico-comp-save-cat-${c.__idx}`}>💾 Salva</button>
              )}
              <button type="button" onClick={() => removeComp(c.__idx)} className="p-1 rounded hover:bg-black/5" data-testid={`dico-comp-del-${c.__idx}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {fields.map(([k, lbl, kind]) => {
                if (kind === "check_esso" || kind === "check_ver") {
                  const suffix = kind === "check_esso" ? "☒ = Eseguito" : "☒ = Verificata";
                  return (
                    <label key={k} className="flex items-start gap-2 text-sm cursor-pointer md:col-span-2">
                      <input type="checkbox" className="mt-0.5" checked={!!c[k]}
                        onChange={(e) => updateComp(c.__idx, k, e.target.checked)}
                        data-testid={`dico-comp-${k}-${c.__idx}`} />
                      <span>{lbl} <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>({suffix})</span></span>
                    </label>
                  );
                }
                return (
                  <Field key={k} label={lbl}>
                    <input type={kind === "num" ? "number" : "text"} step={kind === "num" ? "0.01" : undefined}
                      value={c[k] ?? ""} onChange={(e) => updateComp(c.__idx, k, e.target.value)}
                      className={inputCls} style={inputStyle} data-testid={`dico-comp-${k}-${c.__idx}`} />
                  </Field>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComponentiSottosezioni({ f, set, commessaId, onOpenCatalogo, addBlankComponent, updateComp, removeComp }) {
  const isFotovoltaico = !!(f.tipologia || {}).fotovoltaico;
  const isPompaCalore = !!(f.tipologia || {}).pompa_calore;
  const isClimatizzazione = !!(f.tipologia || {}).climatizzazione;
  const sotto = f.sotto_tipologia || "fotovoltaico_completo";

  // ------- Ramo Pompa di Calore -------
  if (isPompaCalore) {
    return (
      <div>
        <SottoSezioneCategoria titolo="Pompa di Calore" categoria="pompa_calore"
          f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
          onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
          testid="dico-sez-pompa_calore" />
        <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-md border" style={{ borderColor: "var(--border)" }}>
          <input type="checkbox" checked={!!f.has_bollitore}
            onChange={(e) => set("has_bollitore", e.target.checked)}
            data-testid="dico-toggle-bollitore" />
          <span className="text-sm font-semibold">Bollitore / ACS presente</span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>(spunta se presente)</span>
        </div>
        {f.has_bollitore && (
          <SottoSezioneCategoria titolo="Bollitore / ACS" categoria="bollitore"
            f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
            onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
            testid="dico-sez-bollitore" />
        )}
      </div>
    );
  }

  // ------- Ramo Climatizzazione (Unità Interne + Unità Esterna) -------
  if (isClimatizzazione) {
    return (
      <div>
        <SottoSezioneCategoria titolo="Unità Interne" categoria="unita_interna"
          f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
          onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
          testid="dico-sez-unita_interna" />
        <SottoSezioneCategoria titolo="Unità Esterna" categoria="unita_esterna"
          f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
          onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
          testid="dico-sez-unita_esterna" />
      </div>
    );
  }

  // ------- Ramo Fotovoltaico (default) -------
  const showModuliFV = !isFotovoltaico || sotto === "fotovoltaico_completo";
  const showInverter = !isFotovoltaico || ["fotovoltaico_completo", "solo_inverter", "inverter_accumulo"].includes(sotto);
  const showAccumuloForced = isFotovoltaico && ["solo_accumulo", "inverter_accumulo"].includes(sotto);
  const showAccumuloOpzionale = !isFotovoltaico || sotto === "fotovoltaico_completo";
  const showAccumulo = showAccumuloForced || (showAccumuloOpzionale && f.has_accumulo);
  return (
    <div>
      {showModuliFV && (
        <SottoSezioneCategoria titolo="Generatore Fotovoltaico" categoria="modulo"
          f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
          onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
          testid="dico-sez-modulo" />
      )}
      {showInverter && (
        <SottoSezioneCategoria titolo="Inverter Ibrido" categoria="inverter"
          f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
          onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
          testid="dico-sez-inverter" />
      )}
      {showAccumuloOpzionale && !showAccumuloForced && (
        <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-md border" style={{ borderColor: "var(--border)" }}>
          <input type="checkbox" checked={!!f.has_accumulo}
            onChange={(e) => set("has_accumulo", e.target.checked)}
            data-testid="dico-toggle-accumulo" />
          <span className="text-sm font-semibold">Impianto con Sistema di Accumulo</span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>(spunta se presente)</span>
        </div>
      )}
      {showAccumulo && (
        <SottoSezioneCategoria titolo="Sistema di Accumulo" categoria="accumulo"
          f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
          onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
          testid="dico-sez-accumulo" />
      )}
      <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-md border" style={{ borderColor: "var(--border)" }}>
        <input type="checkbox" checked={!!f.has_wallbox}
          onChange={(e) => set("has_wallbox", e.target.checked)}
          data-testid="dico-toggle-wallbox" />
        <span className="text-sm font-semibold">Con Wallbox / EV Charger</span>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>(spunta se presente)</span>
      </div>
      {f.has_wallbox && (
        <SottoSezioneCategoria titolo="Wallbox / EV Charger" categoria="wallbox"
          f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
          onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
          testid="dico-sez-wallbox" />
      )}
      <SottoSezioneCategoria titolo="Protezioni Lato AC" categoria="protezione"
        f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
        onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
        testid="dico-sez-protezione" />
      <SottoSezioneCategoria titolo="Cavi & Linee Elettriche" categoria="cavo"
        f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
        onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
        testid="dico-sez-cavo" />
      <SottoSezioneCategoria titolo="Quadri Elettrici AC/DC" categoria="quadro"
        f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
        onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
        testid="dico-sez-quadro" />
      <SottoSezioneCategoria titolo="Impianto di Terra" categoria="terra"
        f={f} commessaId={commessaId} onOpenCatalogo={onOpenCatalogo}
        onAddBlank={addBlankComponent} updateComp={updateComp} removeComp={removeComp}
        testid="dico-sez-terra" />
    </div>
  );
}

// ---------- DiCo Form ----------
const DicoCheck = ({ checked, onChange, label, testid }) => (
  <label className="flex items-start gap-2 text-sm cursor-pointer">
    <input type="checkbox" className="mt-0.5" checked={!!checked} onChange={onChange} data-testid={testid} />
    <span>{label}</span>
  </label>
);

function DicoFormModal({ cid, cliente, existing, onClose, onSaved }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [catFilterCat, setCatFilterCat] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const commessaId = cliente?.commessa_id;

  useEffect(() => {
    if (existing) { setF({ ...existing, componenti: existing.componenti || [], verifiche: existing.verifiche || {} }); return; }
    api.get("/dico/impresa").then(({ data: imp }) => {
      const nomeCli = [cliente?.nome, cliente?.cognome_ragione_sociale].filter(Boolean).join(" ");
      setF({
        // Solo campi cliente-specifici; l'anagrafica impresa è letta lato PDF dalle impostazioni.
        committente_nome: nomeCli,
        committente_cf: cliente?.cf_piva || "",
        committente_telefono: cliente?.telefono || "",
        immobile_indirizzo: cliente?.indirizzo || "",
        immobile_comune: cliente?.citta || "",
        immobile_provincia: cliente?.provincia || "",
        immobile_cap: cliente?.cap || "",
        immobile_proprieta: nomeCli,
        immobile_uso: "residenziale",
        immobile_note: "",
        descrizione_impianto: "Impianto fotovoltaico connesso alla rete BT",
        potenza_kw: "",
        capacita_kwh_totale: "",
        tipologia: { fotovoltaico: true },
        sotto_tipologia: "fotovoltaico_completo",
        has_accumulo: true,
        has_wallbox: false,
        has_bollitore: false,
        tipo_intervento: "nuova_installazione",
        tipo_intervento_altro: "",
        all_relazione_materiali: true, all_schema: true, all_attestazione_requisiti: true,
        all_progetto: false, all_riferimento_dico_precedenti: false,
        allegati_facoltativi: "",
        componenti: [],
        verifiche: Object.fromEntries(VERIFICHE.map(([k]) => [k, true])),
        luogo: imp.sede_comune || "",
        data_dichiarazione: new Date().toISOString().slice(0, 10),
      });
    }).catch((err) => { toast.error(formatApiError(err)); onClose(); });
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Calcolo automatico totali potenza (kWp) e capacità accumulo (kWh) dalla lista
  // componenti — utile per popolare i campi principali senza doverli inserire a mano.
  const derivedTotals = () => {
    let kwp = 0, kwh = 0;
    for (const c of f?.componenti || []) {
      const cat = c.categoria || "";
      const qty = parseFloat(String(c.quantita || "").replace(",", ".")) || 0;
      if (cat === "modulo") {
        const explicit = parseFloat(String(c.potenza_totale_kw || "").replace(",", ".")) || 0;
        if (explicit) kwp += explicit;
        else {
          const wp = parseFloat(String(c.potenza_wp || "").replace(",", ".")) || 0;
          kwp += (qty * wp) / 1000;
        }
      } else if (cat === "accumulo") {
        const explicit = parseFloat(String(c.capacita_totale_kwh || "").replace(",", ".")) || 0;
        if (explicit) kwh += explicit;
        else {
          const k = parseFloat(String(c.capacita_kwh || "").replace(",", ".")) || 0;
          kwh += qty * k;
        }
      }
    }
    return { kwp: kwp > 0 ? +kwp.toFixed(3) : 0, kwh: kwh > 0 ? +kwh.toFixed(2) : 0 };
  };

  const addComponent = (c) => {
    // Elimina id (è del catalogo) e aggiungi copia embedded
    const { id, commessa_id, created_at, ...rest } = c;
    setF((p) => ({ ...p, componenti: [...(p.componenti || []), { ...rest }] }));
    toast.success("Componente aggiunto alla DiCo");
  };

  const addBlankComponent = (categoria = "modulo") => {
    setF((p) => ({ ...p, componenti: [...(p.componenti || []), { categoria, marca: "", modello: "", quantita: "" }] }));
  };

  const updateComp = (idx, k, v) => {
    setF((p) => {
      const arr = [...(p.componenti || [])];
      arr[idx] = { ...arr[idx], [k]: v };
      return { ...p, componenti: arr };
    });
  };

  const removeComp = (idx) => {
    setF((p) => ({ ...p, componenti: (p.componenti || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!f.descrizione_impianto?.trim()) { toast.error("Inserisci la descrizione dell'impianto"); return; }
    setSaving(true);
    try {
      if (existing) await api.put(`/dico/${existing.id}`, f);
      else await api.post(`/clienti/${cid}/dico`, f);
      toast.success(existing ? "DiCo aggiornata" : "DiCo creata");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const openPreview = async () => {
    if (!f.descrizione_impianto?.trim()) { toast.error("Inserisci la descrizione impianto per l'anteprima"); return; }
    setPreviewLoading(true);
    try {
      const { data } = await api.post(`/dico/preview/${cid}/pdf`, f, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      setPreviewUrl(url);
    } catch (err) { toast.error(formatApiError(err)); } finally { setPreviewLoading(false); }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  if (!f) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)" }} data-testid="dico-form-modal">
      <div className="card-shell w-full max-w-4xl p-6 max-h-[92vh] overflow-y-auto" style={{ background: "#fff" }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>
              {existing ? `Modifica ${existing.numero}` : "Nuova Dichiarazione di Conformità"}
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>D.M. 22 gennaio 2008, n. 37 — art. 7 · Solo i dati del cliente/impianto; l&apos;anagrafica impresa (logo, firme, visura) è già configurata globalmente.</div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-black/5" data-testid="dico-form-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <Accordion title="Committente" defaultOpen testid="committente">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              <Field label="Nominativo"><input value={f.committente_nome || ""} onChange={(e) => set("committente_nome", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-committente_nome" /></Field>
              <Field label="C.F. / P.IVA"><input value={f.committente_cf || ""} onChange={(e) => set("committente_cf", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-committente_cf" /></Field>
              <Field label="Telefono"><input value={f.committente_telefono || ""} onChange={(e) => set("committente_telefono", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-committente_telefono" /></Field>
            </div>
          </Accordion>

          <Accordion title="Immobile / luogo di installazione" defaultOpen testid="immobile">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              <Field label="Indirizzo" className="md:col-span-2"><input value={f.immobile_indirizzo || ""} onChange={(e) => set("immobile_indirizzo", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-immobile_indirizzo" /></Field>
              <Field label="CAP"><input value={f.immobile_cap || ""} onChange={(e) => set("immobile_cap", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-immobile_cap" /></Field>
              <Field label="Comune"><input value={f.immobile_comune || ""} onChange={(e) => set("immobile_comune", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-immobile_comune" /></Field>
              <Field label="Provincia"><input value={f.immobile_provincia || ""} onChange={(e) => set("immobile_provincia", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-immobile_provincia" /></Field>
              <Field label="Di proprietà di"><input value={f.immobile_proprieta || ""} onChange={(e) => set("immobile_proprieta", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-immobile_proprieta" /></Field>
              <Field label="Uso edificio">
                <select value={f.immobile_uso || "residenziale"} onChange={(e) => set("immobile_uso", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-immobile_uso">
                  {["residenziale", "industriale", "commerciale", "altri usi"].map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
              <Field label="Note (scala, piano, interno…)"><input value={f.immobile_note || ""} onChange={(e) => set("immobile_note", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-immobile_note" /></Field>
            </div>
          </Accordion>

          <Accordion title="Impianto (descrizione, tipologia, intervento)" defaultOpen testid="impianto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
              <Field label="Descrizione schematica" className="md:col-span-3">
                <textarea value={f.descrizione_impianto || ""} onChange={(e) => set("descrizione_impianto", e.target.value)} rows={2} className={inputCls} style={inputStyle} data-testid="dico-f-descrizione_impianto" />
              </Field>
              <Field label="Potenza totale (kWp)">
                <div className="flex gap-1">
                  <input type="number" step="0.01" value={f.potenza_kw || ""}
                    placeholder={derivedTotals().kwp ? `Auto: ${derivedTotals().kwp} kWp` : ""}
                    onChange={(e) => set("potenza_kw", e.target.value)}
                    className={inputCls} style={inputStyle} data-testid="dico-f-potenza_kw" />
                  {derivedTotals().kwp > 0 && (
                    <button type="button" onClick={() => set("potenza_kw", String(derivedTotals().kwp))}
                      className="btn-secondary text-[10px] px-2" title="Usa il totale calcolato dai moduli FV"
                      data-testid="dico-f-potenza_kw-auto">🔄</button>
                  )}
                </div>
              </Field>
              <Field label="Accumulo totale (kWh)">
                <div className="flex gap-1">
                  <input type="number" step="0.01" value={f.capacita_kwh_totale || ""}
                    placeholder={derivedTotals().kwh ? `Auto: ${derivedTotals().kwh} kWh` : ""}
                    onChange={(e) => set("capacita_kwh_totale", e.target.value)}
                    className={inputCls} style={inputStyle} data-testid="dico-f-capacita_kwh_totale" />
                  {derivedTotals().kwh > 0 && (
                    <button type="button" onClick={() => set("capacita_kwh_totale", String(derivedTotals().kwh))}
                      className="btn-secondary text-[10px] px-2" title="Usa il totale calcolato dagli accumuli"
                      data-testid="dico-f-capacita_kwh_totale-auto">🔄</button>
                  )}
                </div>
              </Field>
              <Field label="Tipo intervento">
                <select value={f.tipo_intervento || "nuova_installazione"} onChange={(e) => set("tipo_intervento", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-tipo_intervento">
                  {INTERVENTI.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                </select>
              </Field>
              {f.tipo_intervento === "altro" && (
                <Field label="Specificare altro" className="md:col-span-3"><input value={f.tipo_intervento_altro || ""} onChange={(e) => set("tipo_intervento_altro", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-tipo_intervento_altro" /></Field>
              )}
              <div className="col-span-full mt-2">
                <div className="text-xs font-semibold mb-2" style={{ color: "var(--text-muted)" }}>Tipologia impianto (selezione singola)</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {TIPOLOGIE.map(([k, lbl, Icon, color]) => {
                    const selected = !!(f.tipologia || {})[k];
                    const onSelect = () => {
                      // Cambia tipologia e resetta sotto_tipologia coerentemente
                      const defaults = {
                        fotovoltaico: "fotovoltaico_completo",
                        pompa_calore: "pompa_calore",
                      };
                      setF((p) => ({ ...p, tipologia: { [k]: true }, sotto_tipologia: defaults[k] || "" }));
                    };
                    return (
                      <button
                        type="button"
                        key={k}
                        onClick={onSelect}
                        className="rounded-lg border-2 p-4 flex flex-col items-center gap-2 transition-all hover:shadow-md"
                        style={{
                          borderColor: selected ? color : "var(--border)",
                          background: selected ? `${color}12` : "#fff",
                          transform: selected ? "translateY(-1px)" : "none",
                        }}
                        data-testid={`dico-check-${k}`}
                      >
                        <Icon className="w-8 h-8" style={{ color }} strokeWidth={1.6} />
                        <div className="text-sm font-bold text-center" style={{ fontFamily: "Manrope", color: selected ? color : "var(--text)" }}>{lbl}</div>
                        {selected && <div className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: color, color: "#fff" }}>SELEZIONATO</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
              {((f.tipologia || {}).fotovoltaico || (f.tipologia || {}).pompa_calore) && (
                <div className="col-span-full mt-2 p-3 rounded-md" style={{ background: "var(--surface-alt)" }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
                    {(f.tipologia || {}).fotovoltaico ? "Configurazione impianto fotovoltaico" : "Tipo Impianto (Pompa di calore)"}
                  </div>
                  <select
                    value={f.sotto_tipologia || ""}
                    onChange={(e) => set("sotto_tipologia", e.target.value)}
                    className={inputCls}
                    style={inputStyle}
                    data-testid="dico-f-sotto_tipologia"
                  >
                    {((f.tipologia || {}).fotovoltaico ? SOTTO_TIPOLOGIE_FV : SOTTO_TIPOLOGIE_PDC).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                  </select>
                </div>
              )}
            </div>
          </Accordion>

          <Accordion title="Componenti installati" defaultOpen testid="componenti" badge={(f.componenti || []).length || null}>
            {!commessaId && <div className="text-[11px] italic mb-2" style={{ color: "#dc2626" }}>Cliente senza commessa: il catalogo componenti non è disponibile.</div>}
            <ComponentiSottosezioni
              f={f}
              set={set}
              commessaId={commessaId}
              onOpenCatalogo={(cat) => { setCatFilterCat(cat); setCatOpen(true); }}
              addBlankComponent={addBlankComponent}
              updateComp={updateComp}
              removeComp={removeComp}
            />
          </Accordion>

          <Accordion title="Verifiche prima della messa in servizio" testid="verifiche">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pt-1">
              {(() => {
                const tip = f.tipologia || {};
                const lista = tip.pompa_calore ? VERIFICHE_PDC
                  : tip.climatizzazione ? VERIFICHE_CLIMA
                    : VERIFICHE;
                return lista.map(([k, lbl]) => (
                  <DicoCheck key={k} label={lbl}
                    checked={!!(f.verifiche || {})[k]}
                    onChange={(e) => set("verifiche", { ...(f.verifiche || {}), [k]: e.target.checked })}
                    testid={`dico-check-${k}`} />
                ));
              })()}
            </div>
          </Accordion>

          <Accordion title="Allegati" testid="allegati">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pt-1">
              {ALLEGATI.map(([k, lbl]) => (
                <DicoCheck key={k} label={lbl}
                  checked={!!f[k]}
                  onChange={(e) => set(k, e.target.checked)}
                  testid={`dico-check-${k}`} />
              ))}
            </div>
            <Field label="Allegati facoltativi" className="mt-3"><input value={f.allegati_facoltativi || ""} onChange={(e) => set("allegati_facoltativi", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-allegati_facoltativi" /></Field>
          </Accordion>

          <Accordion title="Data e luogo" defaultOpen testid="data-luogo">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              <Field label="Luogo"><input value={f.luogo || ""} onChange={(e) => set("luogo", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-luogo" /></Field>
              <Field label="Data dichiarazione"><input type="date" value={f.data_dichiarazione || ""} onChange={(e) => set("data_dichiarazione", e.target.value)} className={inputCls} style={inputStyle} data-testid="dico-f-data_dichiarazione" /></Field>
            </div>
          </Accordion>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={openPreview} disabled={previewLoading} className="btn-secondary" data-testid="dico-form-preview">
            {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Anteprima
          </button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="dico-form-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (existing ? "Salva modifiche" : "Crea DiCo")}
          </button>
        </div>
      </div>

      {catOpen && commessaId && (
        <CatalogoBrowser
          commessaId={commessaId}
          filterCat={catFilterCat}
          onPick={(c) => { addComponent(c); setCatOpen(false); setCatFilterCat(null); }}
          onClose={() => { setCatOpen(false); setCatFilterCat(null); }}
        />
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-[70] flex items-stretch justify-end" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="dico-preview-modal">
          <div className="w-full max-w-[820px] h-full bg-white flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2 font-bold" style={{ fontFamily: "Manrope" }}>
                <Eye className="w-4 h-4" style={{ color: "var(--primary)" }} /> Anteprima PDF (non salvata)
              </div>
              <div className="flex items-center gap-2">
                <button onClick={openPreview} disabled={previewLoading} className="btn-secondary text-xs" data-testid="dico-preview-refresh">
                  {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "🔄 Aggiorna"}
                </button>
                <a href={previewUrl} target="_blank" rel="noreferrer" className="btn-secondary text-xs" data-testid="dico-preview-open-tab">
                  Apri in nuova scheda
                </a>
                <button onClick={closePreview} className="p-1 rounded hover:bg-black/5" data-testid="dico-preview-close"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <iframe title="Anteprima DiCo" src={previewUrl} className="flex-1 w-full border-0" data-testid="dico-preview-iframe" />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Main block ----------
export default function DicoBlock({ cid, cliente, user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [impresaOpen, setImpresaOpen] = useState(false);
  const [alerts, setAlerts] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/clienti/${cid}/dico`);
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };
  const loadAlerts = async () => {
    try { const { data } = await api.get("/dico/impresa/alerts"); setAlerts(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadAlerts(); /* eslint-disable-next-line */ }, [cid]);

  const openPdf = (d) => {
    const t = localStorage.getItem("access_token");
    window.open(`${API}/dico/${d.id}/pdf${t ? `?auth=${encodeURIComponent(t)}` : ""}`, "_blank");
  };

  const openArchived = async (d) => {
    try {
      const { data } = await api.get(`/dico/${d.id}/archived`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare la ${d.numero}?`)) return;
    try {
      await api.delete(`/dico/${d.id}`);
      toast.success("DiCo eliminata");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [archivingId, setArchivingId] = useState(null);
  const archive = async (d) => {
    setArchivingId(d.id);
    try {
      const { data } = await api.post(`/dico/${d.id}/archive`);
      toast.success(`DiCo archiviata su Aruba Drive: ${data.filename}`);
      // aggiornamento ottimistico dello stato locale
      setItems((prev) => prev.map((it) => it.id === d.id
        ? { ...it, nextcloud_path: data.nextcloud_path, nextcloud_synced_at: data.nextcloud_synced_at }
        : it));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setArchivingId(null);
    }
  };

  return (
    <div className="card-shell p-6" data-testid="dico-block">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}><FileCheck2 className="w-5 h-5 inline mr-2" style={{ color: "var(--primary)" }} />Dichiarazioni di Conformità — DM 37/2008</h2>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>Storico DiCo del cliente: crea, modifica, scarica il PDF pronto da firmare.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === "admin" && (
            <button onClick={() => setImpresaOpen(true)} className="btn-secondary text-xs" data-testid="dico-impresa-btn" title="Dati aziendali validi per TUTTI i clienti (globali)">
              <Building2 className="w-3.5 h-3.5" /> Dati impresa (globali)
            </button>
          )}
          <DicoAssistenteButton cid={cid} onCreated={load} />
          <button onClick={() => { setEditing(null); setFormOpen(true); }} className="btn-primary" data-testid="dico-new-btn">
            <Plus className="w-4 h-4" /> Nuova DiCo
          </button>
        </div>
      </div>

      {alerts && (alerts.is_expired || alerts.is_expiring) && (
        <div
          className="rounded-md border px-4 py-3 mb-4 flex items-center gap-3 text-sm"
          style={{
            borderColor: alerts.is_expired ? "#fca5a5" : "#fcd34d",
            background: alerts.is_expired ? "#fef2f2" : "#fffbeb",
            color: alerts.is_expired ? "#991b1b" : "#92400e",
          }}
          data-testid="dico-visura-alert"
        >
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold">
              {alerts.is_expired
                ? `⚠️ Visura Camerale SCADUTA da ${Math.abs(alerts.days_left)} giorni (${alerts.visura_scadenza})`
                : `⏰ Visura Camerale in scadenza tra ${alerts.days_left} giorni (${alerts.visura_scadenza})`}
            </div>
            <div className="text-xs mt-0.5">
              Aggiorna la visura per continuare ad allegare un documento valido alle DiCo.
            </div>
          </div>
          {user?.role === "admin" && (
            <button onClick={() => setImpresaOpen(true)} className="btn-secondary text-xs" data-testid="dico-visura-fix-btn">
              Aggiorna Visura
            </button>
          )}
        </div>
      )}

      {alerts && alerts.has_visura && !alerts.is_expired && !alerts.is_expiring && alerts.days_left != null && (
        <div className="rounded-md border px-3 py-2 mb-4 flex items-center gap-2 text-xs" style={{ borderColor: "#a7f3d0", background: "#ecfdf5", color: "#065f46" }} data-testid="dico-visura-ok">
          <ShieldCheck className="w-4 h-4" /> Visura Camerale valida (scade fra {alerts.days_left} giorni · {alerts.visura_scadenza})
        </div>
      )}

      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : items.length === 0 ? (
        <div className="text-sm italic py-6 text-center" style={{ color: "var(--text-muted)" }} data-testid="dico-empty">
          Nessuna dichiarazione presente. Crea la prima con &quot;Nuova DiCo&quot;.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-md border px-4 py-3" style={{ borderColor: "var(--border)" }} data-testid={`dico-row-${d.id}`}>
              <div className="flex-1 min-w-[220px]">
                <div className="font-bold text-sm flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                  {d.numero}
                  {d.nextcloud_synced_at && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                      style={{ background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" }}
                      title={`Archiviata su Aruba Drive il ${new Date(d.nextcloud_synced_at).toLocaleString("it-IT")}`}
                      data-testid={`dico-archived-badge-${d.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3" /> Archiviata su Aruba
                    </span>
                  )}
                </div>
                <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                  {d.descrizione_impianto || "—"}{d.potenza_kw ? ` · ${d.potenza_kw} kWp` : ""}{d.capacita_kwh_totale ? ` · ${d.capacita_kwh_totale} kWh` : ""}
                </div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                  del {d.data_dichiarazione ? d.data_dichiarazione.split("-").reverse().join("/") : "—"} · creata da {d.created_by_name || "—"} · {(d.componenti || []).length} componenti
                  {d.nextcloud_synced_at && (
                    <> · <span style={{ color: "#065f46" }}>sincronizzata il {new Date(d.nextcloud_synced_at).toLocaleDateString("it-IT")}</span></>
                  )}
                </div>
                <DicoAiNote d={d} />
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => openPdf(d)} className="btn-secondary text-xs" data-testid={`dico-pdf-${d.id}`}><FileText className="w-3.5 h-3.5" /> PDF</button>
                <button onClick={() => archive(d)} disabled={archivingId === d.id} className="btn-secondary text-xs" data-testid={`dico-archive-${d.id}`} title="Archivia il PDF firmato su Aruba Drive nella cartella del cliente">
                  {archivingId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />} Archivia su Aruba
                </button>
                {d.nextcloud_synced_at && (
                  <button onClick={() => openArchived(d)} className="btn-secondary text-xs" data-testid={`dico-download-archived-${d.id}`} title="Scarica il PDF già archiviato da Aruba Drive senza rigenerarlo">
                    <CloudDownload className="w-3.5 h-3.5" /> Scarica da Aruba
                  </button>
                )}
                <button onClick={() => { setEditing(d); setFormOpen(true); }} className="btn-secondary text-xs" data-testid={`dico-edit-${d.id}`}><Pencil className="w-3.5 h-3.5" /> Modifica</button>
                <button onClick={() => remove(d)} className="btn-secondary text-xs" style={{ color: "#dc2626", borderColor: "#fecaca" }} data-testid={`dico-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /> Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <DicoFormModal cid={cid} cliente={cliente} existing={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }} />
      )}
      {impresaOpen && <ImpresaModal onClose={() => { setImpresaOpen(false); loadAlerts(); }} />}
    </div>
  );
}
